import { defaultFetchHeaders } from "../utils";
import { errorMessageFromResponse, jsonFromResponse } from "../apiError";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const userFinancialBase = `${backend}/user/financial`;

const normalizeStripeSecret = (text: string) => {
  const trimmed = text.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    try {
      return JSON.parse(trimmed) as string;
    } catch {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
};

const fetchStripeSecret = async (url: string) => {
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers : await defaultFetchHeaders(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to fetch Stripe client secret")
    );
  }
  return normalizeStripeSecret(await res.text());
};

const withUserId = (base: string, userId?: string | null) =>
  userId ? `${base}?userId=${encodeURIComponent(userId)}` : base;

export const getPaymentMethodClientSecret = (userId?: string | null) =>
  fetchStripeSecret(withUserId(`${userFinancialBase}/payment_method/client_secret`, userId));

export const getBankAccountClientSecret = (userId?: string | null) =>
  fetchStripeSecret(withUserId(`${userFinancialBase}/bank_account/client_secret`, userId));

export const attachPaymentMethod = async (paymentMethodId: string, userId?: string | null) => {
  const res = await fetch(withUserId(`${userFinancialBase}/attach/payment_method`, userId), {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ paymentMethodId }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to attach payment method")
    );
  }
};

export const attachBankAccount = async (bankAccountId: string, userId?: string | null) => {
  const res = await fetch(withUserId(`${userFinancialBase}/attach/bank_account`, userId), {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ bankAccountId }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to attach bank account")
    );
  }
};

export const getFinancialSummary = async (userId?: string | null) => {
  const res = await fetch(withUserId(`${userFinancialBase}/summary`, userId), {
    method: "GET",
    credentials: "include",
    headers : await defaultFetchHeaders(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to load payment summary")
    );
  }
  const summary = await jsonFromResponse<any>(res, "Failed to load payment summary");
  if (!summary || typeof summary !== "object") return summary;
  const card = summary.card || null;
  const bank = summary.bank || null;
  return {
    paymentMethodId: summary.paymentMethodId ?? null,
    bankAccountId: summary.bankAccountId ?? null,
    paymentMethodLabel: summary.paymentMethodLabel ?? null,
    bankAccountLabel: summary.bankAccountLabel ?? null,
    card: card
      ? {
          ...card,
          expMonth: card.expMonth ?? null,
          expYear: card.expYear ?? null,
        }
      : null,
    bank,
  };
};

export const detachPaymentMethod = async (userId?: string | null) => {
  const res = await fetch(withUserId(`${userFinancialBase}/detach/payment_method`, userId), {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to remove payment method")
    );
  }
};

export const detachBankAccount = async (userId?: string | null) => {
  const res = await fetch(withUserId(`${userFinancialBase}/detach/bank_account`, userId), {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to remove bank account")
    );
  }
};
